import React, { useEffect, useMemo, useRef, useState } from "react";
import OpsRightDrawer from "./components/OpsRightDrawer";
import OpsActionModal from "./components/OpsActionModal";
import {
  opsGetReasons,
  opsListRides,
  opsGetRideDetail,
  opsAddRideNote,
  opsRideReconcile,
  opsAuditLogs,
} from "../../../api/adminOpsApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * ✅ Tabs (monitor-only)
 */
const TABS = [
  { key: "CRITICAL", label: "Critical cases" },
  { key: "ARRIVED", label: "Arrived (not started)" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "ENDED", label: "Ended (24h)" },
  { key: "ISSUES", label: "Issues" },
  { key: "ALL", label: "All rides" },
];

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DT_FMT = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function fmtDT(v) {
  const d = parseDate(v);
  if (!d) return "—";
  const s = DT_FMT.format(d);
  return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
}

function upper(x) {
  return String(x || "").trim().toUpperCase();
}

function minsNum(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : Math.floor(n);
}

function fmtDurationFromMinutes(mins) {
  const m = Math.max(0, Number(mins || 0));
  const day = 24 * 60;
  if (m >= day) return `${Math.floor(m / day)}d`;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}m`;
}

function fmtMins(v) {
  const n = minsNum(v);
  if (n == null) return "—";
  return fmtDurationFromMinutes(n);
}

function Pill({ tone = "slate", children, title, className = "" }) {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
    ink: "bg-slate-900 text-white border-slate-900",
  };
  return (
    <span
      title={title}
      className={cls(
        "inline-flex items-center justify-center text-[11px] px-2 py-1 rounded-md border font-semibold leading-none",
        "whitespace-nowrap",
        map[tone] || map.slate,
        className
      )}
    >
      {children}
    </span>
  );
}

function statusTone(s) {
  const t = upper(s);
  if (t === "IN_PROGRESS") return "violet";
  if (t === "ARRIVED_AT_PICKUP" || t === "ARRIVED") return "amber";
  if (t === "ENDED" || t === "COMPLETED") return "green";
  return "slate";
}

function caseTone(s) {
  const t = upper(s);
  if (t === "OPEN") return "red";
  if (t === "RESOLVED") return "green";
  return "slate";
}

/**
 * ✅ Backend buckets support:
 * - ARRIVED / IN_PROGRESS / ENDED
 * - CRITICAL / ISSUES (derived buckets via OPEN ops cases)
 * - ALL
 */
function backendBucketFor(tabKey) {
  switch (upper(tabKey)) {
    case "CRITICAL":
      return "CRITICAL";
    case "ARRIVED":
      return "ARRIVED";
    case "IN_PROGRESS":
      return "IN_PROGRESS";
    case "ENDED":
      return "ENDED";
    case "ISSUES":
      return "ISSUES";
    case "ALL":
    default:
      return "ALL";
  }
}

function riskBadgeFromRow(row) {
  const sev = Number(row?.riskSeverity || 0);
  const code = row?.riskCode || "";
  const text = row?.riskText || "";
  const label = text || code;

  if (!label || sev <= 0) return { label: "—", tone: "slate" };
  if (sev >= 2) return { label, tone: "red" }; // CRITICAL
  if (sev >= 1) return { label, tone: "amber" }; // ISSUES
  return { label: "—", tone: "slate" };
}

function pingBadge(row) {
  const age = minsNum(row?.lastPingAgeMin);
  if (age == null) return { label: "—", tone: "slate" };
  if (age >= 8) return { label: `STALE ${fmtDurationFromMinutes(age)}`, tone: "red" };
  if (age >= 4) return { label: `LATE ${fmtDurationFromMinutes(age)}`, tone: "amber" };
  return { label: fmtDurationFromMinutes(age), tone: "slate" };
}

function delayBadge(row) {
  const delay = minsNum(row?.dropDelayMin);
  if (delay == null) return { label: "—", tone: "slate" };
  if (delay >= 240) return { label: fmtDurationFromMinutes(delay), tone: "red" };
  if (delay >= 60) return { label: fmtDurationFromMinutes(delay), tone: "amber" };
  return { label: fmtDurationFromMinutes(delay), tone: "slate" };
}

function RowKV({ k, v }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-2 border-b border-slate-200 last:border-b-0">
      <div className="col-span-4 text-xs text-slate-500">{k}</div>
      <div className="col-span-8 text-sm text-slate-900 text-right break-words">{v ?? "—"}</div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {subtitle ? <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-4">{children}</div>
    </div>
  );
}

function safeJson(x) {
  try {
    return JSON.stringify(x ?? null, null, 2);
  } catch {
    return String(x);
  }
}

/**
 * ✅ Reconcile should show only when meaningful:
 * - status is NOT ENDED
 * - expectedEndAt exists
 * - dropDelayMin >= GRACE (120 mins)
 * - status in ARRIVED_AT_PICKUP or IN_PROGRESS
 */
const RECONCILE_GRACE_MIN = 120;
const RECONCILE_ALLOWED = new Set(["ARRIVED_AT_PICKUP", "IN_PROGRESS"]);

function canShowReconcile(ride) {
  if (!ride) return false;
  const st = upper(ride.rideStatus);
  if (!st || st === "ENDED") return false;
  if (!RECONCILE_ALLOWED.has(st)) return false;
  if (!ride.expectedEndAt) return false;
  const delay = minsNum(ride.dropDelayMin);
  if (delay == null) return false;
  return delay >= RECONCILE_GRACE_MIN;
}

/* =============================================================================
   ✅ All rides duration filter (frontend-only for now)
   - Presets + Custom (date inputs)
   - Filters the currently loaded page results (not server-side).
   - ✅ No Apply button, instant filter
   - ✅ Default = MONTH (Last 30 days)
   - ✅ Add "All rides" option (no filtering)
============================================================================= */

const DUR_OPTIONS = [
  { key: "ALL_RIDES", label: "All rides" }, // ✅ NEW (no filter)
  { key: "TODAY", label: "Today" },
  { key: "WEEK", label: "Last 7 days" },
  { key: "MONTH", label: "Last 30 days" }, // ✅ default
  { key: "YEAR", label: "Last 12 months" },
  { key: "CUSTOM", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function computeRange(mode, customFrom, customTo) {
  const now = new Date();
  const today0 = startOfDay(now);

  // ✅ show everything (no filter)
  if (mode === "ALL_RIDES") {
    return { from: null, to: null };
  }

  if (mode === "TODAY") {
    return { from: today0, to: endOfDay(now) };
  }
  if (mode === "WEEK") {
    const from = new Date(today0);
    from.setDate(from.getDate() - 6);
    return { from, to: endOfDay(now) };
  }
  if (mode === "MONTH") {
    const from = new Date(today0);
    from.setDate(from.getDate() - 29);
    return { from, to: endOfDay(now) };
  }
  if (mode === "YEAR") {
    const from = new Date(today0);
    from.setFullYear(from.getFullYear() - 1);
    from.setDate(from.getDate() + 1);
    return { from, to: endOfDay(now) };
  }

  // CUSTOM (allow one-sided ranges too)
  const from = customFrom ? startOfDay(customFrom) : null;
  const to = customTo ? endOfDay(customTo) : null;
  return { from, to };
}

function withinRange(d, range) {
  const x = parseDate(d);
  if (!x) return false;

  // ✅ no constraints => always within
  if (!range?.from && !range?.to) return true;

  const t = x.getTime();
  if (range?.from && t < range.from.getTime()) return false;
  if (range?.to && t > range.to.getTime()) return false;
  return true;
}

export default function OpsRides() {
  const [tab, setTab] = useState("CRITICAL");
  const [q, setQ] = useState("");

  const [busy, setBusy] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);

  const [reasons, setReasons] = useState([]);
  const [modal, setModal] = useState(null);
  const [drawerTab, setDrawerTab] = useState("OVERVIEW");
  const [auditOpen, setAuditOpen] = useState(false);

  // ✅ All rides duration filter state (default = one month)
  const [durMode, setDurMode] = useState("MONTH");
  const [customFromStr, setCustomFromStr] = useState("");
  const [customToStr, setCustomToStr] = useState("");

  const debounceRef = useRef(null);
  const loadSeqRef = useRef(0);
  const autoTickRef = useRef(null);
  const autoDetailTickRef = useRef(null);

  const [countsByTab, setCountsByTab] = useState(() => {
    const obj = {};
    TABS.forEach((t) => (obj[t.key] = 0));
    return obj;
  });

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const s = Number(pageSize || 1);
    return Math.max(1, Math.ceil(t / s));
  }, [total, pageSize]);

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const load = async (queryValue, { silent = false, pageOverride } = {}) => {
    const seq = ++loadSeqRef.current;
    if (!silent) setBusy(true);
    setErr("");

    const query = queryValue ?? q;
    const pageToUse = pageOverride != null ? pageOverride : page;

    try {
      const base = { q: query || "", page: pageToUse, size: pageSize, window: 900 };

      const res = await opsListRides({ ...base, bucket: backendBucketFor(tab) });
      if (seq !== loadSeqRef.current) return;

      const items = Array.isArray(res?.items) ? res.items : [];
      setRows(items);

      const t = Number(res?.total ?? 0);
      setTotal(t);

      const tp = Math.max(1, Math.ceil((t || 0) / (pageSize || 1)));
      if (pageToUse > tp - 1) {
        const newPage = Math.max(0, tp - 1);
        setPage(newPage);
        const rr = await opsListRides({ ...base, page: newPage, bucket: backendBucketFor(tab) });
        if (seq !== loadSeqRef.current) return;
        setRows(Array.isArray(rr?.items) ? rr.items : []);
        setTotal(Number(rr?.total ?? t));
      }

      // counts per tab (size=1)
      const countBase = { q: query || "", page: 0, size: 1, window: 900 };
      const results = await Promise.all(
        TABS.map((t2) => opsListRides({ ...countBase, bucket: backendBucketFor(t2.key) }).catch(() => null))
      );
      if (seq !== loadSeqRef.current) return;

      const nextCounts = {};
      TABS.forEach((t2, i) => {
        const r = results[i];
        nextCounts[t2.key] = Number(r?.total) || Number(r?.items?.length) || 0;
      });
      setCountsByTab((prev) => ({ ...prev, ...nextCounts }));
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setRows([]);
      setTotal(0);
      setErr(e?.message || "Failed to load rides");
    } finally {
      if (seq !== loadSeqRef.current) return;
      if (!silent) setBusy(false);
    }
  };

  const open = async (id, { silent = false } = {}) => {
    setSelectedId(id);
    if (!silent) setErr("");
    setAuditOpen(false);

    try {
      const d = await opsGetRideDetail(id);
      setDetail(d);
    } catch (e) {
      if (!silent) setErr(e?.message || "Failed to load ride detail");
      setDetail(null);
      setAudit([]);
      return;
    }

    try {
      const a = await opsAuditLogs({
        entityType: "RIDE",
        entityId: String(id),
        page: 0,
        size: 50,
      });
      setAudit(a?.content || []);
    } catch {
      setAudit([]);
    }
  };

  const closeDrawer = () => {
    setSelectedId(null);
    setDetail(null);
    setAudit([]);
    setModal(null);
    setDrawerTab("OVERVIEW");
    setAuditOpen(false);
    setErr("");
  };

  // init
  useEffect(() => {
    (async () => {
      try {
        const rr = await opsGetReasons();
        setReasons(rr?.reasons || []);
      } catch {}
      await load("", { silent: false, pageOverride: 0 });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // tab change
  useEffect(() => {
    setPage(0);
    load(q, { silent: false, pageOverride: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // search debounce
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      load(q, { silent: true, pageOverride: 0 });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // page / pageSize change
  useEffect(() => {
    load(q, { silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // auto refresh list
  useEffect(() => {
    if (autoTickRef.current) clearInterval(autoTickRef.current);
    autoTickRef.current = setInterval(() => load(q, { silent: true }), 12000);
    return () => autoTickRef.current && clearInterval(autoTickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, page, pageSize]);

  // auto refresh detail
  useEffect(() => {
    if (autoDetailTickRef.current) clearInterval(autoDetailTickRef.current);
    if (selectedId) {
      autoDetailTickRef.current = setInterval(() => open(selectedId, { silent: true }), 15000);
    }
    return () => autoDetailTickRef.current && clearInterval(autoDetailTickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  function sourceBadge(row) {
    const s = upper(row?.statusSource);
    if (!s || s === "SYSTEM") return { label: "SYSTEM", tone: "slate" };
    if (s === "OPS_RECONCILE_AUTO") return { label: "SETTLEMENT (AUTO)", tone: "amber" };
    if (s === "OPS_RECONCILE_MANUAL") return { label: "SETTLEMENT (MANUAL)", tone: "red" };
    return { label: s, tone: "slate" };
  }

  // ✅ Compute date range for "ALL"
  const customFrom = useMemo(() => (customFromStr ? new Date(`${customFromStr}T00:00:00`) : null), [
    customFromStr,
  ]);
  const customTo = useMemo(() => (customToStr ? new Date(`${customToStr}T00:00:00`) : null), [customToStr]);

  const allRange = useMemo(() => {
    if (upper(tab) !== "ALL") return null;
    return computeRange(durMode, customFrom, customTo);
  }, [tab, durMode, customFrom, customTo]);

  const viewRows = useMemo(() => {
    const xs = Array.isArray(rows) ? rows.slice() : [];

    // ✅ Frontend-only filtering for ALL rides (only if bounds exist)
    let filtered = xs;
    if (upper(tab) === "ALL" && allRange) {
      const hasBounds = !!(allRange.from || allRange.to);
      if (hasBounds) {
        filtered = xs.filter((r) => {
          const ts =
            r?.pickupAt ||
            r?.createdAt ||
            r?.dropAt ||
            r?.plannedEndTime ||
            r?.lastPingAt ||
            null;
          return withinRange(ts, allRange);
        });
      }
    }

    filtered.sort((a, b) => {
      const aOpen = upper(a?.opsCaseStatus) === "OPEN" ? 1 : 0;
      const bOpen = upper(b?.opsCaseStatus) === "OPEN" ? 1 : 0;
      if (bOpen !== aOpen) return bOpen - aOpen;

      const as = Number(a?.riskSeverity || 0);
      const bs = Number(b?.riskSeverity || 0);
      if (bs !== as) return bs - as;

      return Number(b?.rideId || b?.id || 0) - Number(a?.rideId || a?.id || 0);
    });

    return filtered;
  }, [rows, tab, allRange]);

  const headerHint = useMemo(() => {
    if (busy) return "Loading…";
    const t = Number(total || 0);
    const from = t === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(t, (page + 1) * pageSize);
    return `Showing ${from}-${to} of ${t}`;
  }, [busy, total, page, pageSize]);

  const tabMeta = useMemo(() => TABS.find((t) => t.key === tab) || TABS[0], [tab]);
  const overviewLine = useMemo(
    () => `${tabMeta.label} • ${headerHint} • Auto-refresh 12s`,
    [tabMeta.label, headerHint]
  );

  // detail bundle
  const ride = detail?.ride || detail?.row || null;
  const caseDto = detail?.case || null;
  const notes = detail?.notes || [];
  const issues = detail?.issues || [];
  const timeline = detail?.timeline || [];
  const locs = detail?.recentLocations || [];
  const summary = detail?.summary || null;

  const risk = riskBadgeFromRow(ride);
  const ping = pingBadge(ride);
  const delay = delayBadge(ride);

  const drawerSubtitle = useMemo(() => {
    if (!ride) return "";
    const st = ride.rideStatus || "—";
    const last = ride.lastPingAt ? fmtDT(ride.lastPingAt) : "—";
    const ops = ride.opsCaseStatus ? `Case: ${ride.opsCaseStatus}` : "Case: —";
    return `Status: ${st} • ${ops} • Last ping: ${last} • ${ping.label} • Delay: ${delay.label} • Risk: ${risk.label}`;
  }, [ride, ping.label, delay.label, risk.label]);

  const detailJson = useMemo(() => safeJson(detail), [detail]);
  const showReconcile = useMemo(() => canShowReconcile(ride), [ride]);

  // ✅ Tabs layout: full width if possible; fallback to scroll if too tight
  const tabsContainerRef = useRef(null);
  const [tabsOverflow, setTabsOverflow] = useState(false);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;

    const check = () => {
      try {
        setTabsOverflow(el.scrollWidth > el.clientWidth + 4);
      } catch {}
    };

    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
  <div className="rounded-xl border border-slate-200 bg-white">
    <div className="p-4 space-y-3">
            {/* Top line */}
            <div className="flex flex-col lg:flex-row lg:items-start gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-xl font-semibold tracking-tight text-slate-900">Rides</div>
                  <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Live
                  </span>
                </div>

                <div className="mt-1 text-sm text-slate-600">{overviewLine}</div>
                {err ? <div className="mt-2 text-sm text-red-600">{err}</div> : null}
              </div>

              <div className="lg:ml-auto flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 w-full sm:w-[520px]">
                  <span className="text-slate-400 text-sm">⌕</span>
                  <input
                    className="w-full bg-transparent text-sm outline-none"
                    placeholder="Search rideId / bookingId / phone / vehicle reg no"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        clearTimeout(debounceRef.current);
                        setPage(0);
                        load(e.currentTarget.value, { silent: false, pageOverride: 0 });
                      }
                    }}
                  />
                  {q ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-semibold hover:bg-slate-50"
                      onClick={() => {
                        setQ("");
                        setPage(0);
                        load("", { silent: false, pageOverride: 0 });
                      }}
                      title="Clear"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <button
                  className="h-10 rounded-md border border-slate-200 px-4 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
                  disabled={busy}
                  onClick={() => load(q, { silent: false })}
                  type="button"
                  title="Refresh"
                >
                  {busy ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {/* Tabs + All rides filter row */}
            <div className="space-y-2">
              {/* Tabs */}
              <div
                ref={tabsContainerRef}
                className={cls(
                  "w-full",
                  tabsOverflow
                    ? "overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    : ""
                )}
              >
                <div
                  className={cls(
                    tabsOverflow ? "flex gap-2 whitespace-nowrap" : "grid gap-2",
                    tabsOverflow ? "" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
                  )}
                >
                  {TABS.map((x) => {
                    const isActive = tab === x.key;
                    return (
                      <button
                        key={x.key}
                        type="button"
                        onClick={() => setTab(x.key)}
                        className={cls(
                          "h-10 rounded-md border text-sm font-semibold inline-flex items-center justify-center gap-2 px-3",
                          tabsOverflow ? "shrink-0 min-w-[200px]" : "w-full",
                          isActive
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white border-slate-200 hover:bg-slate-50"
                        )}
                      >
                        <span className="truncate">{x.label}</span>
                        <span
                          className={cls(
                            "text-[11px] px-2 py-[3px] rounded-md border",
                            isActive
                              ? "border-white/20 bg-white/10 text-white"
                              : "border-slate-200 bg-slate-50 text-slate-700"
                          )}
                        >
                          {countsByTab[x.key] ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ✅ All rides duration filter (positioned + no Apply + includes ALL_RIDES) */}
              {upper(tab) === "ALL" ? (
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    {/* Left controls */}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <div className="text-xs text-slate-500 sm:mr-2">Filter by duration</div>

                      <div className="relative">
                        <select
                          className={cls(
                            "h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700",
                            "outline-none hover:bg-slate-50 appearance-none"
                          )}
                          value={durMode}
                          onChange={(e) => {
                            const next = e.target.value;
                            setDurMode(next);

                            // keep UX clean when switching away from custom
                            if (next !== "CUSTOM") {
                              setCustomFromStr("");
                              setCustomToStr("");
                            }
                          }}
                        >
                          {DUR_OPTIONS.map((o) => (
                            <option key={o.key} value={o.key}>
                              {o.label}
                            </option>
                          ))}
                        </select>

                        {/* dropdown arrow */}
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                          ▼
                        </div>
                      </div>

                      {/* Custom inputs inline (still no Apply) */}
                      {durMode === "CUSTOM" ? (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:ml-2">
                          <div className="text-xs text-slate-500">From</div>
                          <input
                            type="date"
                            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none"
                            value={customFromStr}
                            onChange={(e) => setCustomFromStr(e.target.value)}
                          />

                          <div className="text-xs text-slate-500">To</div>
                          <input
                            type="date"
                            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none"
                            value={customToStr}
                            onChange={(e) => setCustomToStr(e.target.value)}
                          />

                          {customFromStr || customToStr ? (
                            <button
                              type="button"
                              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold hover:bg-slate-50"
                              onClick={() => {
                                setCustomFromStr("");
                                setCustomToStr("");
                              }}
                              title="Clear custom dates"
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {/* Right range preview */}
                    <div className="text-xs text-slate-500 md:text-right">
                      {allRange?.from || allRange?.to ? (
                        <>
                          Showing:{" "}
                          <span className="font-semibold text-slate-700">
                            {allRange?.from ? fmtDT(allRange.from) : "—"}
                          </span>{" "}
                          →{" "}
                          <span className="font-semibold text-slate-700">
                            {allRange?.to ? fmtDT(allRange.to) : "—"}
                          </span>
                        </>
                      ) : (
                        <span>
                          Showing: <span className="font-semibold text-slate-700">All rides</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto min-w-[1450px]">
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>

            <thead className="bg-slate-50">
              <tr className="text-xs font-semibold tracking-wider text-slate-500">
                <th className="text-left px-5 py-3">Ride</th>
                <th className="text-left px-5 py-3">Booking</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Ops case</th>
                <th className="text-left px-5 py-3">Source</th>
                <th className="text-left px-5 py-3">Driver</th>
                <th className="text-left px-5 py-3">Vehicle</th>
                <th className="text-left px-5 py-3">Ping age</th>
                <th className="text-left px-5 py-3">Delay</th>
                <th className="text-right px-5 py-3">Risk</th>
              </tr>
            </thead>

            <tbody>
              {busy ? (
                <tr>
                  <td colSpan={10} className="px-5 py-4 text-sm text-slate-600">
                    Loading…
                  </td>
                </tr>
              ) : viewRows.length ? (
                viewRows.map((x, idx) => {
                  const id = x.rideId ?? x.id;
                  const bk = x.bookingId;
                  const st = x.rideStatus || "—";
                  const dr = x.driverPhone || "—";
                  const vr = x.vehicleRegNo || "—";

                  const pingP = pingBadge(x);
                  const rk = riskBadgeFromRow(x);
                  const dl = delayBadge(x);

                  const opsCase = x.opsCaseStatus || "—";
                  const rowKey = id != null ? `r-${id}` : `row-${idx}`;

                  const src = sourceBadge(x);
                  const srcTitle =
                    upper(x?.statusSource) === "SYSTEM"
                      ? "Latest status is from normal system/app flow"
                      : `Updated via settlement/reconcile • ${
                          x?.statusSourceAt ? fmtDT(x.statusSourceAt) : "—"
                        }${x?.statusSourceAdminId ? ` • Admin ${x.statusSourceAdminId}` : ""}`;

                  return (
                    <tr
                      key={rowKey}
                      className={cls(
                        "border-t border-slate-200 text-sm cursor-pointer",
                        selectedId === id ? "bg-slate-50" : "hover:bg-slate-50"
                      )}
                      onClick={() => {
                        if (id == null) return;
                        setDrawerTab("OVERVIEW");
                        open(id);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && id != null && open(id)}
                    >
                      <td className="px-5 py-3 font-semibold text-slate-900">
                        {id != null ? `#${id}` : "—"}
                      </td>

                      <td className="px-5 py-3 text-slate-700">{bk != null ? `#${bk}` : "—"}</td>

                      <td className="px-5 py-3">
                        <Pill tone={statusTone(st)}>{st}</Pill>
                      </td>

                      <td className="px-5 py-3">
                        {opsCase === "—" ? (
                          <span className="text-slate-500">—</span>
                        ) : (
                          <Pill
                            tone={caseTone(opsCase)}
                            title={x.opsCaseUpdatedAt ? `Updated: ${fmtDT(x.opsCaseUpdatedAt)}` : ""}
                          >
                            {opsCase}
                          </Pill>
                        )}
                      </td>

                      <td className="px-5 py-3">
                        <Pill tone={src.tone} title={srcTitle}>
                          {src.label}
                        </Pill>
                      </td>

                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{dr}</td>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{vr}</td>

                      <td className="px-5 py-3">
                        {pingP.label === "—" ? (
                          <span className="text-slate-500">—</span>
                        ) : (
                          <Pill tone={pingP.tone}>{pingP.label}</Pill>
                        )}
                      </td>

                      <td className="px-5 py-3">
                        {dl.label === "—" ? (
                          <span className="text-slate-500">—</span>
                        ) : (
                          <Pill tone={dl.tone}>{dl.label}</Pill>
                        )}
                      </td>

                      <td className="px-5 py-3 text-right">
                        {rk.label === "—" ? (
                          <span className="text-slate-500">—</span>
                        ) : (
                          <Pill tone={rk.tone}>{rk.label}</Pill>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="px-5 py-4 text-sm text-slate-600">
                    No rides found for this tab.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination footer (clean + aligned) */}
        <div className="px-5 py-3 border-t border-slate-200 bg-white">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-500">Live updates every 12s</div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
              {/* View per page */}
              <div className="flex items-center gap-2">
                <div className="text-xs text-slate-500">View</div>

                <div className="relative">
                  <select
                    className={cls(
                      "h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700",
                      "outline-none hover:bg-slate-50 appearance-none"
                    )}
                    value={pageSize}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setPageSize(Number.isNaN(n) ? 25 : n);
                      setPage(0);
                    }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}/page
                      </option>
                    ))}
                  </select>

                  {/* dropdown arrow */}
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                    ▼
                  </div>
                </div>
              </div>

              {/* Page info */}
              <div className="text-sm text-slate-700 whitespace-nowrap">
                Page <span className="font-semibold">{page + 1}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </div>

              {/* Prev/Next */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={busy || !canPrev}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                  disabled={busy || !canNext}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <OpsRightDrawer
        open={!!selectedId}
        title={selectedId ? `Ride #${selectedId}` : "Ride"}
        subtitle={drawerSubtitle}
        onClose={closeDrawer}
        width={980}
      >
        <div className="p-5 space-y-4">
          {!ride ? (
            <div className="text-sm text-slate-600">No details loaded.</div>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Ride</div>
                    <div className="text-xs text-slate-500 mt-0.5">Monitor-only • notes are audited</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
                      disabled={!selectedId}
                      onClick={() => selectedId && open(selectedId)}
                      type="button"
                    >
                      Refresh
                    </button>

                    <button
                      className="h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-semibold"
                      onClick={() => setModal({ type: "note", note: "", tags: [] })}
                      type="button"
                    >
                      Add note
                    </button>

                    {showReconcile ? (
                      <button
                        className="h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                        onClick={async () => {
                          if (!selectedId) return;
                          await opsRideReconcile(selectedId);
                          await open(selectedId);
                        }}
                        type="button"
                        title="Syncs status/timestamps from summary signals (after end+grace)."
                      >
                        Sync status from summary
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {["OVERVIEW", "LOCATIONS", "RAW"].map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setDrawerTab(k)}
                        className={cls(
                          "h-9 px-3 rounded-md border text-sm font-semibold",
                          drawerTab === k
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white border-slate-200 hover:bg-slate-50"
                        )}
                      >
                        {k === "RAW" ? "Raw JSON" : k[0] + k.slice(1).toLowerCase()}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <Pill tone={statusTone(ride.rideStatus)}>{ride.rideStatus || "—"}</Pill>
                    {ride.opsCaseStatus ? <Pill tone={caseTone(ride.opsCaseStatus)}>{ride.opsCaseStatus}</Pill> : null}
                    {ping.label !== "—" ? <Pill tone={ping.tone}>{ping.label}</Pill> : null}
                    {delay.label !== "—" ? <Pill tone={delay.tone}>{delay.label}</Pill> : null}
                    {risk.label !== "—" ? <Pill tone={risk.tone}>{risk.label}</Pill> : null}
                  </div>
                </div>
              </div>

              {drawerTab === "OVERVIEW" ? (
                <>
                  <SectionCard
                    title="Settlement"
                    subtitle="Ops case + reconciliation (monitor-only)"
                    right={
                      ride.opsCaseStatus ? (
                        <Pill tone={caseTone(ride.opsCaseStatus)}>{ride.opsCaseStatus}</Pill>
                      ) : (
                        <Pill tone="slate">NO CASE</Pill>
                      )
                    }
                  >
                    <RowKV k="Ops case status" v={caseDto?.caseStatus || ride.opsCaseStatus || "—"} />
                    <RowKV k="Issue code" v={caseDto?.issueCode || ride.riskCode || "—"} />
                    <RowKV k="Issue text" v={caseDto?.issueText || ride.riskText || "—"} />
                    <RowKV k="Status source" v={ride.statusSource ? ride.statusSource : "SYSTEM"} />
                    <RowKV k="Source at" v={ride.statusSourceAt ? fmtDT(ride.statusSourceAt) : "—"} />
                    <RowKV
                      k="Source admin"
                      v={ride.statusSourceAdminId != null ? `Admin ${ride.statusSourceAdminId}` : "—"}
                    />
                    <RowKV k="End derived from summary" v={ride.endDerivedFromSummary ? "Yes" : "No"} />
                    <div className="py-2" />
                    <RowKV k="Expected end at" v={fmtDT(ride.expectedEndAt)} />
                    <RowKV k="Drop delay" v={ride.dropDelayMin != null ? fmtMins(ride.dropDelayMin) : "—"} />
                    <RowKV k="Reconcile shown after" v={`${RECONCILE_GRACE_MIN}m grace`} />
                  </SectionCard>

                  <SectionCard title="Ride details" subtitle="Core monitoring fields">
                    <RowKV k="Ride ID" v={ride.rideId != null ? `#${ride.rideId}` : "—"} />
                    <RowKV k="Booking ID" v={ride.bookingId != null ? `#${ride.bookingId}` : "—"} />
                    <RowKV
                      k="Ride status"
                      v={<Pill tone={statusTone(ride.rideStatus)}>{ride.rideStatus || "—"}</Pill>}
                    />
                    <RowKV k="Last ping at" v={fmtDT(ride.lastPingAt)} />
                    <RowKV k="Last ping age" v={ride.lastPingAgeMin != null ? fmtMins(ride.lastPingAgeMin) : "—"} />
                    <RowKV k="Last lat/lng" v={ride.lastLat != null ? `${ride.lastLat}, ${ride.lastLng}` : "—"} />
                    <RowKV k="Risk code" v={ride.riskCode || "—"} />
                    <RowKV k="Risk text" v={ride.riskText || "—"} />
                    <RowKV k="Risk severity" v={ride.riskSeverity != null ? String(ride.riskSeverity) : "—"} />
                    <RowKV k="Pickup at" v={fmtDT(ride.pickupAt)} />
                    <RowKV k="Drop at" v={fmtDT(ride.dropAt)} />
                    <RowKV k="Planned end time" v={fmtDT(ride.plannedEndTime)} />
                  </SectionCard>

                  <SectionCard title="Contacts" subtitle="User/driver/owner">
                    <RowKV k="User" v={`${ride.userName || "—"} • ${ride.userPhone || "—"}`} />
                    <RowKV k="Driver" v={`${ride.driverName || "—"} • ${ride.driverPhone || "—"}`} />
                    <RowKV k="Owner" v={`${ride.ownerName || "—"} • ${ride.ownerPhone || "—"}`} />
                    <RowKV k="Vehicle" v={ride.vehicleRegNo || "—"} />
                  </SectionCard>

                  <SectionCard title="Summary" subtitle="Ride summary if created">
                    <RowKV k="Distance (km)" v={summary?.distanceKm ?? "—"} />
                    <RowKV k="Duration (hrs)" v={summary?.durationHours ?? "—"} />
                    <RowKV k="Actual start" v={fmtDT(summary?.actualStartTime)} />
                    <RowKV k="Actual end" v={fmtDT(summary?.actualEndTime)} />
                  </SectionCard>

                  <SectionCard title="Issues" subtitle="Derived issues (monitor-only)">
                    <div className="py-3 space-y-2">
                      {issues.length ? (
                        issues.slice(0, 20).map((x, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 p-3">
                            <div className="text-sm font-semibold text-slate-900">{x.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{x.detail}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">No issues.</div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard title="Notes" subtitle="Internal ops notes">
                    <div className="py-3 space-y-2">
                      {notes.length ? (
                        notes.slice(0, 25).map((n) => (
                          <div key={String(n.id)} className="rounded-lg border border-slate-200 p-3">
                            <div className="text-xs text-slate-500">
                              {fmtDT(n.createdAt)} • Admin {n.adminId ?? "—"} • {n.reasonCode || "—"}
                            </div>
                            <div className="mt-1 text-sm text-slate-700">{n.note || "—"}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">No notes.</div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard
                    title="Audit"
                    subtitle="Admin audit logs (collapsed by default)"
                    right={
                      <button
                        type="button"
                        className={cls(
                          "h-9 px-3 rounded-md border text-sm font-semibold",
                          auditOpen
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white border-slate-200 hover:bg-slate-50"
                        )}
                        onClick={() => setAuditOpen((x) => !x)}
                      >
                        {auditOpen ? "Hide" : `Show (${audit.length || 0})`}
                      </button>
                    }
                  >
                    {!auditOpen ? (
                      <div className="py-3 text-sm text-slate-600">
                        Hidden to reduce noise. Expand only when needed.
                      </div>
                    ) : (
                      <div className="py-3 space-y-2">
                        {audit.length ? (
                          audit.slice(0, 50).map((a) => (
                            <div key={String(a.id)} className="rounded-lg border border-slate-200 p-3">
                              <div className="text-xs text-slate-500">
                                {fmtDT(a.createdAt)} • {a.actionKey}
                              </div>
                              <div className="mt-1 text-sm text-slate-700">{a.reason || "—"}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-slate-600 py-3">No audit logs.</div>
                        )}
                      </div>
                    )}
                  </SectionCard>

                  <SectionCard title="Timeline" subtitle="Ops timeline (lightweight)">
                    <div className="py-3 space-y-2">
                      {timeline.length ? (
                        timeline.slice(0, 30).map((t, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 p-3">
                            <div className="text-xs text-slate-500">
                              {fmtDT(t.at)} • {t.type}
                            </div>
                            <div className="mt-1 text-sm text-slate-700">{t.message || "—"}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-slate-600 py-3">No timeline items.</div>
                      )}
                    </div>
                  </SectionCard>
                </>
              ) : drawerTab === "LOCATIONS" ? (
                <SectionCard title="Recent locations" subtitle="Last points (newest → oldest)">
                  <div className="py-3 space-y-2">
                    {locs.length ? (
                      locs.slice(0, 200).map((p, idx) => (
                        <div key={idx} className="rounded-lg border border-slate-200 p-3">
                          <div className="text-xs text-slate-500">{fmtDT(p.capturedAt)}</div>
                          <div className="mt-1 text-sm text-slate-700">
                            {p.lat}, {p.lng}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            acc: {p.accuracy ?? "—"} • spd: {p.speed ?? "—"} • hdg: {p.heading ?? "—"}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-slate-600 py-3">No location points.</div>
                    )}
                  </div>
                </SectionCard>
              ) : (
                <SectionCard title="Raw JSON" subtitle="Full ride bundle">
                  <pre className="px-4 py-4 text-[12px] leading-relaxed overflow-auto max-h-[520px] bg-white text-slate-800">
                    {detailJson}
                  </pre>
                </SectionCard>
              )}
            </>
          )}
        </div>
      </OpsRightDrawer>

      {/* Modal (note only) */}
      <OpsActionModal
        open={!!modal}
        title={"Add internal note"}
        hint="Reason mandatory • changes are audit logged."
        reasons={(reasons || []).map((r) => ({ code: r.code, label: r.label }))}
        confirmLabel={"Save note"}
        onClose={() => setModal(null)}
        onConfirm={async (reason) => {
          if (!selectedId) return;
          await opsAddRideNote(selectedId, modal?.note || "", modal?.tags || [], reason);
          await open(selectedId);
          setModal(null);
        }}
      >
        <div>
          <div className="text-sm text-slate-700">Note</div>
          <textarea
            className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[90px]"
            value={modal?.note || ""}
            onChange={(e) => setModal({ ...modal, note: e.target.value })}
            placeholder="What happened + next step…"
          />
        </div>
      </OpsActionModal>
    </div>
  );
}
